import { Refresh as RefreshIcon } from '@mui/icons-material'
import { Box, Typography, Button, Alert } from '@mui/material'
import { Component, ReactNode, ErrorInfo } from 'react'

import ErrorHandler from '../../services/ErrorHandler'

interface CalendarErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

interface CalendarErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Error boundary component specifically for calendar feature
 * Handles calendar-specific errors gracefully with user-friendly messages
 *
 * Requirements: 7.1, 7.3
 */
class CalendarErrorBoundary extends Component<
  CalendarErrorBoundaryProps,
  CalendarErrorBoundaryState
> {
  constructor(props: CalendarErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): CalendarErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error using our centralized error handler
    const appError = ErrorHandler.createError(
      'system',
      `Calendar component error: ${error.message}`,
      { error, errorInfo }
    )
    ErrorHandler.handleError(appError)

    this.setState({
      error,
      errorInfo
    })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default calendar error UI
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            p: 3,
            textAlign: 'center'
          }}
        >
          <Alert severity='error' sx={{ mb: 2, width: '100%', maxWidth: 500 }}>
            <Typography variant='h6' gutterBottom>
              Calendar Error
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              We're having trouble loading your calendar. This might be due to a
              network issue or a temporary problem with your booking data.
            </Typography>
          </Alert>

          <Button
            variant='contained'
            startIcon={<RefreshIcon />}
            onClick={this.handleRetry}
            sx={{ mt: 1 }}
          >
            Try Again
          </Button>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography
                variant='caption'
                component='pre'
                sx={{ fontSize: '0.75rem' }}
              >
                {this.state.error.message}
                {this.state.errorInfo?.componentStack}
              </Typography>
            </Box>
          )}
        </Box>
      )
    }

    return this.props.children
  }
}

export default CalendarErrorBoundary
